/**
 * TanStack Query hooks for all REST endpoints.
 * See spec §1.5.6 for the endpoint list.
 */
import { useNow } from '@/features/oi/useNow';
import { config } from '@/lib/config';
import { buildOiDetailFromSummary, buildOiSummaryFromDrone } from '@/lib/oi/droneOi';
import { composeOis } from '@/lib/oi/mergeOis';
import { bboxKey, squareBbox } from '@/lib/oi/searchArea';
import { queryClient } from '@/lib/query';
import { type DroneLive, useDronesStore } from '@/lib/store/drones';
import { type OiSearchArea, useOiStore } from '@/lib/store/oi';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useShallow } from 'zustand/shallow';
import {
  type AircraftBufferKey,
  type CustodianConfigPatch,
  type CustodianDroneConfig,
  type CustodianPilot,
  type DaaRuleRecord,
  addDaaRule,
  deleteDaaRule,
  fetchActivePilots,
  fetchCustodianDrone,
  fetchCustodianDroneConfig,
  fetchCustodianDrones,
  fetchCustodianHealth,
  fetchCustodianMission,
  personalityLabel,
  putAdapterType,
  putAircraftBuffer,
  putCustodianConfig,
  putOwnershipFlag,
  toInventoryUser,
} from './custodian';
import {
  type FlightFilters,
  fetchFlightTelemetry,
  fetchFlights,
  fetchReservedFlights,
  telemetryWindow,
} from './flights';
import { http } from './http';
import { fetchOwnOiDeclarations } from './oiDeclarations';
import { searchExternalOis } from './oiSearch';
import type {
  DssOi,
  Flight,
  InfraSite,
  InventoryDrone,
  InventoryUser,
  OiDetail,
  OiSummary,
  TerrainDataSource,
  TerrainResponse,
} from './types';

const STALE = {
  registry: 5 * 60_000,
  flights: 60_000,
  telemetry: 5 * 60_000,
  oiList: 60_000,
  oiDetail: 30_000,
  dss: 30_000,
  terrain: 24 * 3600_000,
};

/** The tenant flight list, in one cache entry per filter set.
 *
 *  Per-aircraft grouping, per-flight detail and the nav badge all derive from
 *  this with useMemo — there is deliberately no second query and no
 *  per-flight fetch, because `/flights` already returns the complete record
 *  inline. Opening a detail view is a cache read, not a network call.
 *
 *  `filters` maps onto the server-side params (spec §2.1) and is part of the
 *  query key, so each distinct filter set caches separately and switching
 *  back to a previous one is instant. Omitted ⇒ the whole history, which is
 *  what every caller did before the filters existed.
 *
 *  Mock mode ignores `filters`: MSW serves the fixture list whole and the
 *  Flights context narrows it in the UI, so demo behaviour is unchanged.
 *
 *  This is a POLL-AND-CACHE context, not a live-stream one. No MQTT topic
 *  carries flight history (see the design doc §2.6), so an in-progress
 *  flight's row updates on refetch rather than in real time. */
export function useFlights(opts?: { enabled?: boolean; filters?: FlightFilters }) {
  const filters = opts?.filters ?? {};
  return useQuery({
    // Live mode → real Custodian API (via same-origin proxy); demo → MSW mock.
    // Filters are keyed as a stable JSON string: callers build the object
    // inline, so a structural key would be a fresh reference every render.
    queryKey: ['flights', config.dataSource, stableKey(filters)],
    queryFn: async () => {
      if (!config.isMock) return fetchFlights(filters);
      return (await http.get('api/flights').json<{ flights: Flight[] }>()).flights;
    },
    staleTime: STALE.flights,
    // Callers inside the Flights context leave this alone. NavRail must NOT
    // trigger it — see `useReservedFlights`.
    enabled: opts?.enabled ?? true,
  });
}

/** Key-sorted JSON, so two filter objects with the same contents in a
 *  different literal order share one cache entry. Empty ⇒ `''`, which keeps
 *  the unfiltered key identical to what it was before filters existed. */
function stableKey(filters: FlightFilters): string {
  const entries = Object.entries(filters)
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]): [string, string] => [k, v instanceof Date ? v.toISOString() : String(v)])
    .sort(([a], [b]) => a.localeCompare(b));
  return entries.length === 0 ? '' : JSON.stringify(entries);
}

/** Nav-badge-sized companion to `useFlights` — the planned/scheduled set only.
 *
 *  Live-only: in demo the full list is served locally by MSW at no network
 *  cost, so the badge just reads that. */
export function useReservedFlights({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['flights', 'reserved', config.dataSource],
    queryFn: fetchReservedFlights,
    enabled: enabled && !config.isMock,
    staleTime: STALE.flights,
  });
}

/** One flight's telemetry history — `GET /api/v1/telemetry`, live only.
 *
 *  Deliberately NOT folded into `useFlights`: the response is the flight's
 *  full InfluxDB history (~10k points, ~10 s on the wire), so it is fetched
 *  only while a detail view is open and never for the list. Demo keeps its
 *  fixture track inline on the flight record, so this stays disabled there.
 *
 *  A completed flight's history is immutable, hence the long staleTime and
 *  no refetch on focus — a 10 s call has no business re-firing every time
 *  the operator tabs back. One retry, not the default three, for the same
 *  reason: three failed attempts is half a minute of a spinner. */
export function useFlightTelemetry(flight: Flight | null) {
  const guid = flight?.flight_id ?? null;
  // A planned flight has never flown, so there is nothing to query for.
  const planned = flight?.source === 'planned';
  const window = useMemo(() => (flight ? telemetryWindow(flight) : {}), [flight]);

  return useQuery({
    queryKey: ['flight-telemetry', guid, window.from ?? '', window.to ?? ''],
    queryFn: () => fetchFlightTelemetry(guid as string, window),
    enabled: !config.isMock && guid != null && !planned,
    staleTime: STALE.telemetry,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

/** Demo-only. Live has no DroneLogBook sync endpoint, so `SyncFlightDialog`
 *  never calls this in live mode — it shows the pending-backend explainer. */
export function useSyncFlight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ flightId }: { flightId: string }) =>
      http.post(`api/flights/${flightId}/sync`).json<Flight>(),
    onSuccess: (updated) => {
      // Patch the one row rather than invalidating the whole list — a refetch
      // would flash the table back to skeletons over a single-record write.
      // Same reasoning as useSaveCustodianConfig.
      qc.setQueryData(['flights', config.dataSource], (old: Flight[] | undefined) =>
        old?.map((f) => (f.flight_id === updated.flight_id ? updated : f)),
      );
    },
  });
}

export function useInfraSites() {
  return useQuery({
    queryKey: ['infra', 'sites'],
    queryFn: async () => (await http.get('api/infra/sites').json<{ sites: InfraSite[] }>()).sites,
    staleTime: STALE.registry,
  });
}

/** The Inventory Users tab.
 *
 *  Live mode reads `GET /pilots/active` — the Custodian's DLB pass-through
 *  (ClickUp 86e30bd6t). It used to have no live source at all (the Custodian
 *  was aircraft-only), so the tab was mock-only and simply empty against a
 *  real backend.
 *
 *  It shares the Default Pilot picker's query key deliberately: same URL, and
 *  that call costs 13-20 s upstream, so two cache entries would mean paying it
 *  twice on one panel. `select` shapes the same cached payload per consumer.
 *
 *  Two honest limits, both from the endpoint's own filter — it is a roster of
 *  ACTIVE Pilot-role users, not every account: the Inactive chip matches
 *  nothing, and so does Admin (see `toInventoryUser` on why `is_admin` is not
 *  inferred). Mock mode keeps the full fixture, unchanged. */
export function useInventoryUsers() {
  const mock = useQuery({
    queryKey: ['inventory', 'users'],
    queryFn: async () =>
      (await http.get('api/inventory/users').json<{ users: InventoryUser[] }>()).users,
    staleTime: STALE.registry,
    enabled: config.isMock,
  });
  const live = useQuery({
    queryKey: ['inventory', 'pilots'],
    queryFn: fetchActivePilots,
    select: (pilots: CustodianPilot[]) => pilots.map(toInventoryUser),
    enabled: !config.isMock,
    staleTime: STALE.registry,
    retry: false,
  });
  return config.isMock ? mock : live;
}

/** `GET /pilots/active` — active, Pilot-role DLB users, for the drone detail
 *  panel's Default Pilot picker (ClickUp 86e30bd6t).
 *
 *  Tenant-wide and small (13 on production), so it's one shared cache entry
 *  rather than a per-drone fetch, cached on the registry stale time like the
 *  other reference lists. Live-only: mock mode has no such endpoint and the
 *  panel falls back to its stand-in list there.
 *
 *  Not retried. The two documented failures — 503 (no DLB credentials on the
 *  custodian) and 502 (DLB itself unreachable) — are both states that persist
 *  for far longer than a retry window, so retrying only delays the fallback
 *  the panel already has.
 */
export function useActivePilots() {
  return useQuery<CustodianPilot[]>({
    queryKey: ['inventory', 'pilots'],
    queryFn: fetchActivePilots,
    enabled: !config.isMock,
    staleTime: STALE.registry,
    retry: false,
  });
}

export function useInventoryDrones({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    enabled,
    // Live mode → real Custodian API (via same-origin proxy); demo → MSW mock.
    queryKey: ['inventory', 'drones', config.dataSource],
    queryFn: async () => {
      if (!config.isMock) return fetchCustodianDrones();
      return (await http.get('api/inventory/drones').json<{ drones: InventoryDrone[] }>()).drones;
    },
    staleTime: STALE.registry,
    // ENG-265 — DJI status lights are time-sensitive (Telemetry goes stale in
    // 30 s), so refresh only when the fleet actually has a DJI aircraft.
    refetchInterval: (q) => (q.state.data?.some((d) => d.dji_status != null) ? 30_000 : false),
  });
}

/** `GET /config` for one drone — the detail panel's Aircraft / Custodian
 *  Configuration sections. Mock mode has no equivalent endpoint (the mock
 *  plane's InventoryDrone already carries full values), so this only runs
 *  in live mode. */
export function useCustodianDroneConfig(serial: string | null) {
  return useQuery({
    queryKey: ['inventory', 'drone-config', serial],
    queryFn: () => fetchCustodianDroneConfig(serial as string),
    enabled: serial != null && !config.isMock,
    staleTime: STALE.registry,
  });
}

/** `PUT /ownership` — see `putOwnershipFlag` for the `reason` caveat.
 *
 *  Writes the confirmed-saved value directly into the `/config` cache with
 *  `setQueryData` instead of `invalidateQueries`. Invalidating triggers an
 *  async background `GET /config` — there's a real window between "mutation
 *  resolved, caller flips `dirty` false" and "that GET actually lands"
 *  where the cache still holds the pre-save value. The detail panel's
 *  resync effect (keyed off `dirty`) fires the instant `dirty` goes false,
 *  reads whatever's in the cache at that exact moment, and — if the GET
 *  hasn't landed yet — snaps the toggle back to the old value for a frame
 *  before the GET resolves and it flips again. That's the on-then-off
 *  flash a real toggle showed. `setQueryData` is synchronous and uses the
 *  exact value the server just confirmed accepting, so there's no gap for
 *  a refetch to race. The drones list is patched the same way rather than
 *  invalidated — see `useSaveCustodianConfig` for why a full refetch is the
 *  wrong tool for a single-drone write. */
export function useSetOwnershipFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      serial,
      enabled,
      reason,
    }: { serial: string; enabled: boolean; reason: string }) =>
      putOwnershipFlag(serial, enabled, reason),
    onSuccess: (_data, vars) => {
      qc.setQueryData(
        ['inventory', 'drone-config', vars.serial],
        (old: CustodianDroneConfig | undefined) =>
          old ? { ...old, ownership_flag_enabled: vars.enabled } : old,
      );
      // Keep the one list record in step too, so the value is right even on
      // the first frame after a remount, before `/config` is consulted.
      qc.setQueryData(
        ['inventory', 'drones', config.dataSource],
        (old: InventoryDrone[] | undefined) =>
          old?.map((d) =>
            d.serial_number === vars.serial ? { ...d, ownership_flag_enabled: vars.enabled } : d,
          ),
      );
    },
  });
}

/** `PUT /adapter-config` — change one aircraft's adapter type (ENG-228).
 *
 *  Writes the value we sent into both caches instead of refetching, for the
 *  same reasons as `useSetOwnershipFlag`: a refetch of the whole fleet blanks
 *  the left list, and it races the detail panel's resync. The endpoint's
 *  response isn't documented, so the caches record what the request carried.
 *  The list record also gets the derived `personality` label, since that is what
 *  the card chip reads. */
export function useSetAdapterType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ serial, adapterType }: { serial: string; adapterType: string }) =>
      putAdapterType(serial, adapterType),
    onSuccess: (_data, vars) => {
      qc.setQueryData(
        ['inventory', 'drone-config', vars.serial],
        (old: CustodianDroneConfig | undefined) =>
          old ? { ...old, adapter_type: vars.adapterType } : old,
      );
      qc.setQueryData(
        ['inventory', 'drones', config.dataSource],
        (old: InventoryDrone[] | undefined) =>
          old?.map((d) =>
            d.serial_number === vars.serial
              ? {
                  ...d,
                  adapter_type: vars.adapterType,
                  personality: personalityLabel(vars.adapterType),
                }
              : d,
          ),
      );
    },
  });
}

/** Clear an aircraft's geofence / obstacle buffer back to "not set" (ENG-295).
 *
 *  Setting a value rides along with `PUT /config` (`useSaveCustodianConfig`);
 *  only a clear needs this dedicated endpoint, because `/config` ignores null.
 *  Both caches are written with `null` rather than invalidated, for the reasons
 *  `useSetAdapterType` gives — and because the panel merges `config ?? list`,
 *  so a list entry left at the old value would put a cleared 30 m back on screen.
 *
 *  Callers must let this finish BEFORE a `useSaveCustodianConfig` in the same
 *  Save: that hook re-reads the drone afterwards and overwrites the list entry,
 *  and a read that overtook the clear would bring the old value back. */
export function useClearAircraftBuffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ serial, key }: { serial: string; key: AircraftBufferKey }) =>
      putAircraftBuffer(serial, key, null),
    onSuccess: (_data, vars) => {
      qc.setQueryData(
        ['inventory', 'drone-config', vars.serial],
        (old: CustodianDroneConfig | undefined) => (old ? { ...old, [vars.key]: null } : old),
      );
      qc.setQueryData(
        ['inventory', 'drones', config.dataSource],
        (old: InventoryDrone[] | undefined) =>
          old?.map((d) => (d.serial_number === vars.serial ? { ...d, [vars.key]: null } : d)),
      );
    },
  });
}

/** `GET /health` — GCS-plugin session/version for the open drone. Polls,
 *  since it's the freshest connectivity signal the API exposes. */
export function useCustodianHealth(serial: string | null) {
  return useQuery({
    queryKey: ['inventory', 'drone-health', serial],
    queryFn: () => fetchCustodianHealth(serial as string),
    enabled: serial != null && !config.isMock,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/** `GET /drones/{serial}` — one aircraft's inventory record (make, model, type,
 *  GCS plugin, adapter…) for the open drone. Per-serial on purpose: the drone
 *  panel is public, and the full fleet list is not (ENG-281). Live only; demo
 *  mode already carries these fields on the telemetry. */
export function useCustodianDrone(serial: string | null) {
  return useQuery({
    queryKey: ['inventory', 'drone', serial],
    queryFn: () => fetchCustodianDrone(serial as string),
    enabled: serial != null && !config.isMock,
    staleTime: STALE.registry,
    retry: false,
  });
}

/** `GET /{serial}/mission` — the active mission binding for the open drone.
 *
 *  Lazy per-open-drone, same as `/config` and `/health`: fetching it for all 27
 *  rows would be an N+1 for data only the detail panel shows. Resolves to `null`
 *  when the drone has no active binding (the 404 case, which is 24 of 27), so
 *  `data === null` means "no binding" and `isError` means something actually
 *  went wrong — the panel distinguishes the two.
 *
 *  Polled on the same 30 s cadence as `/health` because `last_message_at` and
 *  `oi_window.utm_state` move while a flight is running. */
export function useCustodianMission(serial: string | null) {
  return useQuery({
    queryKey: ['inventory', 'drone-mission', serial],
    queryFn: () => fetchCustodianMission(serial as string),
    enabled: serial != null && !config.isMock,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/** What the detail panel's Save collected. One `PUT /config` covers every
 *  configurable field now; ownership stays separate because `/config`
 *  ignores it and `/ownership` needs an audit `reason`. */
export interface CustodianSave {
  serial: string;
  patch: CustodianConfigPatch;
}

/** Saves via a single `PUT /config`, then reconciles that ONE drone from
 *  `GET /drones/{serial}`.
 *
 *  Deliberately does NOT `invalidateQueries` the drones list. Doing so
 *  refetched the entire fleet and — because the panel showed skeletons for
 *  any `isFetching` — blanked the whole left list on every save. It was also
 *  racy for the same reason `useSetOwnershipFlag` avoids invalidation: the
 *  detail panel's resync effect fires the instant `dirty` clears and reads
 *  the drone straight out of the list cache, which still held pre-save
 *  values until the refetch landed, so freshly-saved fields visibly snapped
 *  back for a frame. Both caches are instead written from the responses the
 *  server just confirmed.
 *
 *  A failed reconcile does not fail the save — the PUT already succeeded, so
 *  the list is left for the next natural refetch rather than reporting a
 *  false error. */
export function useSaveCustodianConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: CustodianSave) => {
      const cfg = await putCustodianConfig(v.serial, v.patch);
      try {
        return { cfg, fresh: await fetchCustodianDrone(v.serial) };
      } catch {
        return { cfg, fresh: null };
      }
    },
    onSuccess: ({ cfg, fresh }, v) => {
      // `/config`'s own response is authoritative — no hand-written merge.
      qc.setQueryData(['inventory', 'drone-config', v.serial], cfg);
      if (!fresh) return;
      qc.setQueryData(
        ['inventory', 'drones', config.dataSource],
        (old: InventoryDrone[] | undefined) =>
          old?.map((d) => (d.serial_number === v.serial ? fresh : d)),
      );
    },
  });
}

/* ────────────────────────────────────────────────────────────────────────
 * DAA rules (spec §1.5). Deliberately NOT part of `PUT /config` — the two
 * endpoints below are the only write path, and each returns the full updated
 * array.
 *
 * Both write straight into the `/config` cache entry rather than invalidating
 * it, for the same reason `useSaveCustodianConfig` does: the detail panel
 * derives its whole view from that entry via `applyConfigToInventoryDrone`,
 * and a refetch flashes the section back to skeletons over a single-rule
 * change. The server's array is authoritative, so it replaces ours wholesale.
 * ──────────────────────────────────────────────────────────────────────── */

function writeDaaRules(
  qc: ReturnType<typeof useQueryClient>,
  serial: string,
  rules: DaaRuleRecord[],
) {
  qc.setQueryData(['inventory', 'drone-config', serial], (old: CustodianDroneConfig | undefined) =>
    old ? { ...old, daa_rules: rules } : old,
  );
}

/** `POST /daa/add_rule`. The rule object is free-form; the server stamps the
 *  `id`, so the caller never supplies one. */
export function useAddDaaRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ serial, rule }: { serial: string; rule: DaaRuleRecord }) =>
      addDaaRule(serial, rule),
    onSuccess: (rules, v) => writeDaaRules(qc, v.serial, rules),
  });
}

/** `DELETE /daa/rules/{rule_id}`. `ruleId` must be the server's own `id` —
 *  `toDaaRule` synthesises an `R{n}` placeholder when a record has none, and
 *  such a rule cannot be deleted (the UI hides the control for it). */
export function useDeleteDaaRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ serial, ruleId }: { serial: string; ruleId: string }) =>
      deleteDaaRule(serial, ruleId),
    onSuccess: (rules, v) => writeDaaRules(qc, v.serial, rules),
  });
}

export function useOiList() {
  return useQuery({
    queryKey: ['oi', 'list'],
    queryFn: async () => (await http.get('api/oi').json<{ items: OiSummary[] }>()).items,
    staleTime: STALE.oiList,
    // Real backend doesn't expose /api/oi — hitting it returns 405 and
    // sprays console errors before MQTT can populate the live list.
    // `useOiListLive()` below merges MQTT-derived OIs anyway, so the
    // REST call is purely a mock-mode concern.
    enabled: config.isMock,
  });
}

const HOUR_MS = 3_600_000;

/** End of the scrubber's 24 h window (top of the current hour + 24 h). */
export function externalOisWindowEnd(nowMs: number): number {
  return Math.floor(nowMs / HOUR_MS) * HOUR_MS + 24 * HOUR_MS;
}

/** Keyed by the rounded bbox and the window-end hour, so the key only rolls
 *  over on the hour or when the area moves. Stays under 'oi' so the OI map
 *  layer's query-cache subscription redraws. */
export function externalOisKey(area: OiSearchArea, nowMs: number) {
  if (!area.center) return ['oi', 'dss', 'none'] as const;
  return [
    'oi',
    'dss',
    bboxKey(squareBbox(area.center, area.sizeKm)),
    externalOisWindowEnd(nowMs),
  ] as const;
}

/** 3rd-Party ("External") OIs from a live DSS region search (ENG-183).
 *  Mock mode is served by the MSW `/oi-api` handler — same code path. */
export function useExternalOis() {
  const category = useOiStore((s) => s.category);
  const area = useOiStore(useShallow((s) => s.searchArea));
  const now = Date.now();
  return useQuery({
    queryKey: externalOisKey(area, now),
    queryFn: async () => {
      if (!area.center) return [];
      return searchExternalOis(squareBbox(area.center, area.sizeKm), externalOisWindowEnd(now));
    },
    // All keeps the results live once a 3rd Party search area exists.
    enabled: (category === '3rdparty' || category === 'all') && area.center !== null,
    staleTime: STALE.dss,
    refetchInterval: 60_000,
    // A rejected bbox won't fix itself on retry.
    retry: 1,
    placeholderData: keepPreviousData,
  });
}

/** Non-React read for the Mapbox layers. While a new area is loading, returns
 *  the most recent previous results so polygons don't blink out mid-drag. */
export function getCurrentExternalOis(): DssOi[] {
  const s = useOiStore.getState();
  if ((s.category !== '3rdparty' && s.category !== 'all') || !s.searchArea.center) return [];
  const exact = queryClient.getQueryData<DssOi[]>(externalOisKey(s.searchArea, Date.now()));
  if (exact) return exact;
  const latest = queryClient
    .getQueryCache()
    .findAll({ queryKey: ['oi', 'dss'] })
    .filter((q) => Array.isArray(q.state.data))
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)[0];
  return (latest?.state.data as DssOi[] | undefined) ?? [];
}

export function useOiDetail(id: string | null, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['oi', 'detail', id],
    queryFn: () => http.get(`api/oi/${id}`).json<OiDetail>(),
    enabled: id != null && config.isMock && enabled,
    staleTime: STALE.oiDetail,
  });
}

/* ────────────────────────────────────────────────────────────────────────
 * OI live-mode bridge — ticket 86e1hnwf5 item 2.
 *
 * In live mode the REST endpoints `/api/oi` and `/api/oi/<id>` don't
 * exist (MSW only runs in mock mode; the real backend doesn't expose
 * them). But each drone's MQTT `flightplan/<UAS_ID>` payload carries
 * the polygon + schedule + declaration_id, projected into
 * `drone.oi` on `useDronesStore`.
 *
 * The two hooks below let the OI panel surfaces (OIList card / OIDetail
 * pane / OI map layer) read from whichever source has data:
 *   useOiListLive()    — REST cache + per-drone MQTT OI envelopes,
 *                        deduped on `aircraft` (drone_id). REST wins
 *                        when both have a record for the same drone.
 *   useOiDetailLive(id) — REST detail when available; otherwise build
 *                        an OiDetail synthesised from the drone whose
 *                        `oi.oi_id` matches the requested id. Fields
 *                        the broker doesn't ship (DSS / USS / pilot /
 *                        op_type / etc.) appear as '—' or null,
 *                        same convention used elsewhere in live mode.
 * ──────────────────────────────────────────────────────────────────── */

function buildOiDetailFromDrone(d: DroneLive): OiDetail | null {
  const summary = buildOiSummaryFromDrone(d);
  const oi = d.oi;
  if (!summary || !oi || !oi.polygon) return null;

  const durationMin =
    oi.start_utc && oi.end_utc
      ? Math.max(0, Math.round((Date.parse(oi.end_utc) - Date.parse(oi.start_utc)) / 60000))
      : 0;

  // Polygon bounds from the outer ring.
  const coords = oi.polygon.coordinates[0] ?? [];
  let minLng = Number.POSITIVE_INFINITY;
  let maxLng = Number.NEGATIVE_INFINITY;
  let minLat = Number.POSITIVE_INFINITY;
  let maxLat = Number.NEGATIVE_INFINITY;
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  const polygonBounds: [number, number, number, number] =
    coords.length > 0 ? [minLng, minLat, maxLng, maxLat] : [0, 0, 0, 0];

  // Max AGL comes from the AGL source only. It used to fall back to
  // `altitude_ceiling_m`, which is an MSL/W84 volume ceiling — mixing the two
  // is the same datum error the OI panel had, one row down.
  const maxAglM = oi.agl_ceiling_ft != null ? Math.round(oi.agl_ceiling_ft / 3.28084) : null;

  return {
    ...summary,
    polygon: oi.polygon,
    polygon_bounds: polygonBounds,
    polygon_vertex_count: coords.length,
    operation: oi.op_type ?? '—',
    mission_id: null,
    flight_id: oi.oi_ref_id,
    plan_id: null,
    // ── DSS / USS rows. Every one of these was hardcoded '—' until the
    //    broker started publishing `operational_intent_reference` on
    //    `oi/declaration` (2026-08-27). They still fall back to '—' on the
    //    legacy `flightplan/` topic, which carries none of it. ──
    dss_status: oi.oir?.state ?? '—',
    dss_status_code: oi.rtm_state,
    oi_ref_id: oi.oi_ref_id ?? oi.oi_id,
    ovn: oi.oir?.ovn ?? '—',
    // Null rather than 0 when the volume band isn't published: a confident
    // "0.00 m" floor is indistinguishable from a real sea-level floor.
    alt_lower_m: oi.altitude_floor_m,
    alt_upper_m: oi.altitude_ceiling_m,
    alt_datum: oi.altitude_datum,
    max_agl_m: maxAglM,
    uss_availability: oi.oir?.uss_availability ?? '—',
    uss_base_url: oi.oir?.uss_base_url ?? '—',
    subscription_id: oi.oir?.subscription_id ?? null,
    subscribers: [],
    duration_min: durationMin,
    submission_status: oi.ops_approval,
    // The DECLARATION id, which is what `oi_id` holds — kept distinct from
    // `oi_ref_id`, which now prefers the RTM operational intent id.
    flight_decl_id: oi.oi_id,
    manager_uss: oi.oir?.manager ?? null,
    last_updated: oi.received_at,
    time_added_total_s: (oi as any).time_added_total_s ?? null,
    time_added_max_s: (oi as any).time_added_max_s ?? null,
    time_amend_enabled: (oi as any).time_amend_enabled ?? null,
  };
}

/** Our own OIs from FlightBlender, polled every minute (ENG-313). Both data
 *  planes use it — demo mode is served by the MSW `/oi-api` handler. A failure
 *  leaves `data` undefined so the list falls back to MQTT alone. */
export function useBlenderOis() {
  return useQuery({
    queryKey: ['oi', 'blender'],
    queryFn: () => fetchOwnOiDeclarations(Date.now()),
    staleTime: 55_000,
    refetchInterval: 60_000,
    retry: 1,
  });
}

/** The OI list: MQTT preferred, FlightBlender as the schedule-based source and
 *  fallback (see `composeOis`). REST results (demo mode) win for the same OI. */
export function useOiListLive() {
  const rest = useOiList();
  const blender = useBlenderOis();
  const dronesArr = useDronesStore(useShallow((s) => Object.values(s.byId)));
  // MQTT silence and OI windows are time-based — re-evaluate between events.
  const now = useNow(15_000);

  const data = useMemo(
    () => composeOis({ rest: rest.data ?? [], blender: blender.data, drones: dronesArr, now }),
    [rest.data, blender.data, dronesArr, now],
  );

  return {
    data,
    // In live mode the REST query is disabled (see useOiList), so its
    // `isPending` flag is meaningless. Treat the bridge as "loaded" the
    // moment any MQTT data arrives; otherwise mirror REST while it runs.
    isPending: config.isMock ? rest.isPending && data.length === 0 : false,
    // Same reasoning for errors — REST 405 in live mode is not a real
    // failure the user should see. MQTT silence is shown as an empty
    // list ("No OIs match current filters"), not a red error banner.
    isError: config.isMock ? rest.isError && data.length === 0 : false,
    refetch: rest.refetch,
  };
}

/** Like `useOiDetail(id)` but falls back to building an OiDetail from
 *  the drone whose `oi.oi_id` matches `id` when REST returns nothing. */
export function useOiDetailLive(id: string | null) {
  const { data: list } = useOiListLive();
  const listed = id ? list.find((o) => o.oi_id === id && !o.aircraft) : undefined;
  // A Scheduled OI exists only in Blender — there is no REST detail to ask for.
  const rest = useOiDetail(id, { enabled: !listed });
  const matchedDrone = useDronesStore((s) =>
    id ? (Object.values(s.byId).find((d) => d.oi?.oi_id === id) ?? null) : null,
  );

  if (rest.data) return rest;

  if (matchedDrone) {
    const synthesised = buildOiDetailFromDrone(matchedDrone);
    if (synthesised) {
      return {
        ...rest,
        data: synthesised,
        isPending: false,
        isError: false,
      };
    }
  }
  // A Scheduled OI has no aircraft yet (ENG-313) — build its detail from the
  // composed list entry (Blender's declaration).
  if (listed) {
    const fromSummary = buildOiDetailFromSummary(listed);
    if (fromSummary) return { ...rest, data: fromSummary, isPending: false, isError: false };
  }
  // Live mode has no REST detail: the query is disabled, and a disabled
  // query sits in `pending` forever. With no drone carrying the id either,
  // the OI is gone — report "settled, no data" so the pane doesn't pulse
  // skeletons indefinitely (ENG-206).
  return config.isMock ? rest : { ...rest, isPending: false };
}

export function useTerrain(lat: number | null, lng: number | null) {
  return useQuery({
    queryKey: ['terrain', lat, lng],
    queryFn: () =>
      http
        .get('api/terrain', { searchParams: { lat: String(lat), lng: String(lng) } })
        .json<TerrainResponse>(),
    enabled: lat != null && lng != null,
    staleTime: STALE.terrain,
  });
}

/** Static-ish list of the elevation/terrain data sources used by the
 *  backend. Drives the Terrain tab "Elevation Data Sources" accordion.
 *  Static enough to cache for a day. */
export function useTerrainSources() {
  return useQuery({
    queryKey: ['terrain', 'sources'],
    queryFn: async () =>
      (await http.get('api/terrain/sources').json<{ sources: TerrainDataSource[] }>()).sources,
    staleTime: STALE.terrain,
  });
}
